import { UserAssessmentWithDetailsResponse } from '@api/assessmentQuestionnaireApiHandler';
import { BoostersAndDrainers } from '@api/boostersAndDrainersApiHandler';
import {
  HistoryItem,
  IndicatorHistoryResponse,
  IndicatorStatusEnum,
  QuestionnaireIndicator,
} from '@api/indicatorsApiHandler';
import SpeakUpChatIcon from '@assets/images/earlyWarning/chat-icon.svg';
import IndicatorIcon from '@assets/images/icons/indicator-icon.svg';
import InsightsOutlinedIcon from '@assets/images/icons/insights-outlined.svg';
import LightBulbIcon from '@assets/images/icons/lightbulb-outlined.svg';
import TrendingDownIcon from '@assets/images/icons/trending-down-outlined.svg';
import TrendingUpIcon from '@assets/images/icons/trending-up-outlined.svg';
import { DEFAULT_V2_IMAGE_PATH } from '@config/questionnaire-v2-images.config';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { MotionImage, MotionText, showToast } from '@shared/atoms';
import { AreaChart, CreateSpeakUp, IndicatorAreaChart, SystemButton } from '@shared/molecules';
import { formatDateToShortMonth } from '@utils/date.util';
import { ToastMessages } from 'constants/toastMessages';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { RootState } from 'store';
import { SCORE_LABEL_RANGES, ScoreColor, ScoreLabel } from './configs';
import styles from './styles.module.scss';

interface InsightsSectionProps {
  assessmentData: UserAssessmentWithDetailsResponse[] | [];
  boostersAndDrainers: BoostersAndDrainers | null;
  isIndicatorEnabled: boolean;
  wellbeingIndicatorDetails: IndicatorHistoryResponse | null;
  wellbeingIndicatorAssessment: QuestionnaireIndicator | null;
  insightTexts: string[] | [];
  userTakenSpecializedAssessmentInIndicator: HistoryItem | null;
}

export const InsightsSection: React.FC<InsightsSectionProps> = ({
  assessmentData,
  boostersAndDrainers,
  isIndicatorEnabled,
  wellbeingIndicatorDetails,
  wellbeingIndicatorAssessment,
  insightTexts,
  userTakenSpecializedAssessmentInIndicator,
}) => {
  const history = useHistory();
  const { isDesktop } = useWindowDimensions();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const questionnaireId = params.get('questionnaireId');
  const { chartSelectedAssessment, chartSelectedIndicator } = useAppSelector(
    (state) => state.assessmentScores || {},
  );
  const [speakUpOpen, setSpeakUpOpen] = useState(false);
  const companyConfigData = useAppSelector((state: RootState) => state.companyConfig);

  const currentAssessmentDetails = chartSelectedAssessment?.index
    ? assessmentData[chartSelectedAssessment?.index]
    : assessmentData[0] || null;

  const rawScore = isIndicatorEnabled
    ? chartSelectedIndicator?.score || 0
    : chartSelectedAssessment?.score || 0;

  const score = Number.isInteger(rawScore) ? rawScore : parseFloat(rawScore.toFixed(2));

  const getScoreColor = (sc: number, isHolisticReverse: boolean): string => {
    if (sc >= 70) return !isHolisticReverse ? ScoreColor.Excellent : ScoreColor.NeedsAttention;
    if (sc >= 30) return ScoreColor.Moderate;
    if (sc < 29) return !isHolisticReverse ? ScoreColor.NeedsAttention : ScoreColor.Excellent;

    return ScoreColor.Neutral;
  };

  const getIndicatorColors = (
    status: IndicatorStatusEnum | undefined,
    positive: boolean | undefined,
  ): string => {
    switch (status) {
      case IndicatorStatusEnum.High:
        return positive ? ScoreColor.Excellent : ScoreColor.NeedsAttention;
      case IndicatorStatusEnum.Low:
        return positive ? ScoreColor.NeedsAttention : ScoreColor.Excellent;
      default:
        return ScoreColor.Moderate;
    }
  };

  const getIndicatorLabel = (
    status: IndicatorStatusEnum | undefined,
    name: string | undefined,
  ): string => {
    switch (status) {
      case IndicatorStatusEnum.High:
        return `High ${name ? name.toLowerCase() : ''}`;
      case IndicatorStatusEnum.Low:
        return `Low ${name ? name.toLowerCase() : ''}`;
      default:
        return `Moderate ${name ? name.toLowerCase() : ''}`;
    }
  };

  const getScoreLabel = (sc: number): ScoreLabel => {
    const range = SCORE_LABEL_RANGES.find((r) => sc >= r.min && sc <= r.max);

    return range ? range.label : ScoreLabel.NeedsAttention;
  };

  const getScoreDegree = (sc: number) => (sc / 100) * 360;

  const isHolisticReverse = assessmentData[0]?.questionnaireV2?.reverseScore || false;

  const scoreColor = isIndicatorEnabled
    ? getIndicatorColors(chartSelectedIndicator?.status, chartSelectedIndicator?.isPositive)
    : getScoreColor(score, isHolisticReverse);

  const scoreDegree = getScoreDegree(score);

  const insightIcon = isIndicatorEnabled
    ? wellbeingIndicatorAssessment?.questionnaire?.icon &&
      wellbeingIndicatorAssessment?.questionnaire?.icon
      ? `${DEFAULT_V2_IMAGE_PATH}/${wellbeingIndicatorAssessment?.questionnaire?.icon}`
      : IndicatorIcon
    : currentAssessmentDetails?.questionnaireV2?.icon
    ? `${DEFAULT_V2_IMAGE_PATH}/${currentAssessmentDetails?.questionnaireV2?.icon}`
    : IndicatorIcon;

  const labelForScore = isIndicatorEnabled
    ? getIndicatorLabel(chartSelectedIndicator?.status, chartSelectedIndicator?.indicatorName)
    : getScoreLabel(score);

  const completedDetails = isIndicatorEnabled
    ? chartSelectedIndicator?.scoreSourceText
    : `Based on your ${chartSelectedAssessment?.questionnaireName} Assessment${
        chartSelectedAssessment?.date
          ? ` completed on ${formatDateToShortMonth(chartSelectedAssessment.date)}`
          : ''
      }`;

  // DISABLED MAX SCORE FROM DB FOR INDICATORS, FUTURE REFERENCE
  const maxScore = isIndicatorEnabled
    ? // ? wellbeingIndicatorAssessment?.questionnaire?.maxScore || 100
      100
    : assessmentData[0]?.questionnaireV2?.maxScore || 100;

  return (
    <div className={styles.insightsContainer}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.icon}>
            <MotionImage
              src={insightIcon}
              alt=""
              aria-hidden="true"
              className={styles.topIconImage}
            />
          </div>

          <MotionText
            id="insights-heading"
            className={styles.sectionTitle}
            as="h3"
            text={`Your ${
              isIndicatorEnabled
                ? chartSelectedIndicator?.indicatorName
                : `${chartSelectedAssessment?.questionnaireName} Wellbeing`
            } Insights`}
          />
        </div>
        <div className={styles.headerButtons}>
          <SystemButton
            fullWidthButton={!isDesktop}
            className={styles.retakeBtn}
            size="medium"
            onClick={() => {
              if (isIndicatorEnabled) {
                if (wellbeingIndicatorAssessment?.questionnaireId) {
                  history.push(
                    `/assessment?questionnaireId=${wellbeingIndicatorAssessment?.questionnaireId}&indicatorId=${wellbeingIndicatorAssessment?.indicatorId}`,
                  );
                } else {
                  showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'info');
                }
              } else {
                history.push(`/assessment?questionnaireId=${questionnaireId}`);
              }
            }}
          >
            {isIndicatorEnabled
              ? userTakenSpecializedAssessmentInIndicator
                ? `Re-take ${
                    userTakenSpecializedAssessmentInIndicator?.indicator?.name ||
                    chartSelectedIndicator?.indicatorName ||
                    ''
                  } Assessment`
                : `Take ${chartSelectedIndicator?.indicatorName || ''} Assessment`
              : 'Re-take Assessment'}
          </SystemButton>

          {companyConfigData?.data?.showSpeakUp && (
            <SystemButton
              fullWidthButton={!isDesktop}
              className={styles.speakUpBtn}
              variant="outlinedPrimarySkyBlue"
              size="medium"
              onClick={() => setSpeakUpOpen(true)}
            >
              <MotionImage
                src={SpeakUpChatIcon}
                alt=""
                aria-hidden="true"
                className={styles.speakUpIcon}
              />
              Speak Up
            </SystemButton>
          )}
        </div>
      </div>

      {/* Score Section */}
      <div className={styles.scoreSection}>
        <div
          className={styles.scoreCircle}
          style={{
            background: `conic-gradient(${scoreColor} 0deg ${scoreDegree}deg, #f3f4f6 ${scoreDegree}deg 360deg)`,
            borderColor: scoreColor,
          }}
        >
          <MotionText as="h4" className={styles.scoreNumber} text={score} />
          <MotionText as="text" className={styles.scoreTotal} text={`/${maxScore}`} />
        </div>
        <div className={styles.scoreInfo}>
          <MotionText as="h4" text={labelForScore} />
          <MotionText as="text" text={completedDetails} />
        </div>
      </div>

      {/* Key Insights */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.lightbulbIcon}>
            <MotionImage src={LightBulbIcon} alt="" aria-hidden="true" className={styles.icon} />
          </div>
          <MotionText as="h4" text="Key insights" />
        </div>
        <div className={styles.insightsText}>
          {insightTexts?.length > 0 ? (
            insightTexts.map((text, index) => <MotionText key={index} as="text" text={text} />)
          ) : (
            <MotionText as="text" text="No insight found!" />
          )}
        </div>
      </div>

      {/* Trend Chart */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <div className={styles.chartIcon}>
            <MotionImage
              src={InsightsOutlinedIcon}
              alt=""
              aria-hidden="true"
              className={styles.icon}
            />
          </div>
          <MotionText as="h4" text="Your trend over time" />
        </div>
        <div className={styles.chartContainer}>
          {isIndicatorEnabled ? (
            <IndicatorAreaChart scores={wellbeingIndicatorDetails?.history || []} />
          ) : (
            <AreaChart assessments={assessmentData} />
          )}
        </div>
      </div>

      {/* Mental Boosters */}
      {boostersAndDrainers &&
        !isIndicatorEnabled &&
        boostersAndDrainers?.topBoosters &&
        boostersAndDrainers?.topBoosters.filter(
          (bs) => bs?.question?.label && bs.question?.label?.trim().length > 0,
        )?.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.boostIcon}>
                <MotionImage
                  src={TrendingUpIcon}
                  alt=""
                  aria-hidden="true"
                  className={styles.icon}
                />
              </div>
              <MotionText
                as="h4"
                text={`Your ${chartSelectedAssessment?.questionnaireName?.toLowerCase()} boosters`}
              />
            </div>
            <div className={styles.tagsContainer}>
              {boostersAndDrainers?.topBoosters
                .filter((bs) => bs?.question?.label && bs.question?.label?.trim().length > 0)
                .map((booster, index) => (
                  <span key={index} className={`${styles.tag} ${styles.boosterTag}`}>
                    {booster?.question?.label}
                  </span>
                ))}
            </div>
          </div>
        )}

      {/* Mental Drainers */}
      {boostersAndDrainers &&
        !isIndicatorEnabled &&
        boostersAndDrainers?.topDrainers &&
        boostersAndDrainers?.topDrainers?.filter(
          (bs) => bs?.question?.label && bs.question?.label?.trim().length > 0,
        )?.length > 0 && (
          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.drainIcon}>
                <MotionImage
                  src={TrendingDownIcon}
                  alt=""
                  aria-hidden="true"
                  className={styles.icon}
                />
              </div>

              <MotionText
                as="h4"
                text={`Your ${chartSelectedAssessment?.questionnaireName?.toLowerCase()} drainers`}
              />
            </div>
            <div className={styles.tagsContainer}>
              {boostersAndDrainers?.topDrainers
                .filter((bs) => bs?.question?.label && bs.question?.label?.trim().length > 0)
                .map((drainer, index) => (
                  <span key={index} className={`${styles.tag} ${styles.drainerTag}`}>
                    {drainer?.question?.label}
                  </span>
                ))}
            </div>
          </div>
        )}

      {speakUpOpen && (
        <CreateSpeakUp
          createSpeakUpOpen={speakUpOpen}
          handleSpeakUpModalOpen={setSpeakUpOpen}
          assessmentId={isIndicatorEnabled ? null : chartSelectedAssessment?.assessmentId || null}
          questionnaireId={
            isIndicatorEnabled
              ? wellbeingIndicatorAssessment?.questionnaireId || null
              : questionnaireId
              ? Number(questionnaireId)
              : null
          }
        />
      )}
    </div>
  );
};
